/**
 * AG-Kit BaseTool to LangChain Tool Converter and Vice Versa
 */
import { tool as langChainTool, StructuredTool } from "@langchain/core/tools";
import {
  BaseTool,
  tool as agKitTool,
  ToolResult,
  ToolExecutionContext,
} from "@cloudbase/agent-tools";
import { z } from "zod/v4";

/**
 * Convert AG-Kit BaseTool to LangChain DynamicStructuredTool
 *
 * @param agkitTool - AG-Kit BaseTool instance
 * @param implFunc - Optional custom implementation function
 * @returns LangChain DynamicStructuredTool instance
 */
export function convert2LangChain(
  agkitTool: BaseTool,
  implFunc?: (tool: BaseTool) => Function
) {
  const func =
    implFunc ||
    (async (input: any) => {
      const result = await agkitTool.invoke(input);
      return result;
    });

  return langChainTool(func as any, {
    name: agkitTool.name,
    description: agkitTool.description || agkitTool.name,
    schema: (agkitTool as any).schema || z.object({}),
  });
}

/**
 * Convert LangChain Tool to AG-Kit DynamicTool
 *
 * @param langchainTool - LangChain tool instance (DynamicStructuredTool or StructuredTool)
 * @returns AG-Kit DynamicTool instance
 */
export function convertLangChain2AGKit(langchainTool: StructuredTool) {
  const func: any = async (
    input: Record<string, any>,
    context?: ToolExecutionContext
  ) => {
    try {
      // Call the LangChain tool
      const result = await langchainTool.invoke(input);

      // Return successful result
      return new ToolResult({
        success: true,
        data: result,
      });
    } catch (error) {
      // Handle errors and return ToolResult with error
      return new ToolResult({
        success: false,
        error: error instanceof Error ? error.message : String(error),
        error_type: "execution",
      });
    }
  };

  return agKitTool(func, {
    name: langchainTool.name,
    description: langchainTool.description || langchainTool.name,
    schema: (langchainTool as any).schema || (z.object({}) as z.ZodObject<any>),
  });
}

